import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import RaaAuthenticatedRemoteService from '../../../utility/RaaAuthenticatedRemoteService';
import envConfig from '../../../envConfig';

const remoteService = RaaAuthenticatedRemoteService.globalInstance();

const formatMetricValue = (value) => {
  if (value === null || value === undefined) return '--';

  return new Intl.NumberFormat('en-US', {
    notation: value >= 10000 ? 'compact' : 'standard',
    maximumFractionDigits: 1,
  }).format(value);
};

const buildTrialMetrics = (stats) => [
  {
    value: formatMetricValue(stats.longestDeviceUptimeDays),
    label: 'Days Protected',
    description: 'Consecutive days your environment has been protected.',
  },
  {
    value: formatMetricValue(stats.pushingSensorCount),
    label: 'Assets Covered',
    description: 'Sensors currently monitoring your environment.',
  },
  {
    value: formatMetricValue(stats.estimatedHourlyChannelCount),
    label: 'Readings Collected',
    description: 'Data points recorded from your environment.',
  },
  {
    value: formatMetricValue(stats.configuredAlertCount),
    label: 'Conditions Monitored',
    description: 'Alert thresholds being monitored.',
  },
  {
    value: formatMetricValue(stats.notificationCount),
    label: 'Messages Sent',
    description: 'Notifications generated by alert conditions.',
  },
  {
    value: formatMetricValue(stats.allTriggeredAlertActionCount),
    label: 'Team Members Informed',
    description: 'Users on your account with important access.',
  },
];

const TrialBillingOverview = ({
  servicePlan,
  onSubscribe,
  onComparePlans,
}) => {
  const [stats, setStats] = useState({});
  const [statsLoading, setStatsLoading] = useState(true);
  const [statsError, setStatsError] = useState(null);

  useEffect(() => {
    let isMounted = true;

    const fetchStats = async () => {
      setStatsLoading(true);
      setStatsError(null);

      try {
        const response = await remoteService.get(
          `${envConfig.apiUrl}/api/v1/billing/service-plan/stats`,
        );

        const result = response && response.data ? response.data : response;
        const nextStats = result
        && result.stats
          ? result.stats
          : null;

        if (!result || !nextStats) {
          throw new Error('Failed to load trial metrics.');
        }

        if (isMounted) {
          setStats(nextStats);
        }
      } catch (error) {
        if (isMounted) {
          setStatsError(
            error && error.message
              ? error.message
              : 'Failed to load trial metrics.',
          );
        }
      } finally {
        if (isMounted) {
          setStatsLoading(false);
        }
      }
    };

    fetchStats();

    return () => {
      isMounted = false;
    };
  }, []);

  const planName = servicePlan && servicePlan.description
    ? servicePlan.description
    : 'Enterprise Free Trial';

  const trialMetrics = buildTrialMetrics(stats);

  return (
    <>
      <div className="container-fluid px-4 mt-4">
        <div className="border border-success rounded bg-light px-3 py-3 mb-4">
          <div className="d-flex align-items-center flex-wrap">
            <strong className="text-success mr-3">
              Current Plan
            </strong>

            <span className="badge badge-success px-3 py-2 mr-3">
              {planName}
              {' '}
              Free Test
            </span>

            <button
              type="button"
              className="btn btn-link btn-sm p-0"
              onClick={onComparePlans}
            >
              compare plans
            </button>
          </div>
        </div>

        <div className="row">
          <div className="col-lg-7 mb-4">
            <div className="card h-100">
              <div className="card-body text-center d-flex flex-column justify-content-center py-5">
                <h3 className="mb-4">
                  Subscribe now and get
                  <br />
                  <b>50% off</b>
                  {' '}
                  your 1st Year.
                </h3>

                <div className="mb-2">
                  <span className="text-danger font-weight-bold" style={{ fontSize: '2.5rem' }}>
                    $37
                  </span>
                  <sup className="text-danger font-weight-bold" style={{ fontSize: '1.25rem' }}>
                    50
                  </sup>
                </div>

                <div className="text-muted mb-1">
                  per month, billed annually.
                </div>

                <div className="small text-muted mb-4">
                  Renews at $75 per month, billed annually.
                </div>

                <div>
                  <button
                    type="button"
                    className="btn btn-primary px-4"
                    onClick={onSubscribe}
                  >
                    Subscribe Now
                  </button>
                </div>

                <div className="mt-3">
                  <button
                    type="button"
                    className="btn btn-link btn-sm"
                    onClick={onComparePlans}
                  >
                    See other plans and pricing options.
                  </button>
                </div>
              </div>
            </div>
          </div>

          <div className="col-lg-5 mb-4">
            {statsError && (
              <div className="alert alert-warning mb-3" role="alert">
                {statsError}
              </div>
            )}

            {trialMetrics.map((metric) => (
              <div className="d-flex align-items-center mb-3" key={metric.label}>
                <div
                  className="bg-light border rounded text-center font-weight-bold mr-3 py-2"
                  style={{ minWidth: '90px', fontSize: '1.5rem' }}
                >
                  {statsLoading ? '...' : metric.value}
                </div>

                <div>
                  <h5 className="mb-0">
                    {metric.label}
                  </h5>
                  <div className="text-muted small">
                    {metric.description}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </>
  );
};

TrialBillingOverview.propTypes = {
  servicePlan: PropTypes.shape({
    description: PropTypes.string,
    recurringAmount: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    recurringInterval: PropTypes.string,
    renewalDate: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    nextPaymentDue: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    credit: PropTypes.number,
    pastDue: PropTypes.bool,
    subscriptionTier: PropTypes.shape({
      name: PropTypes.string,
    }).isRequired,
  }),
  onSubscribe: PropTypes.func.isRequired,
  onComparePlans: PropTypes.func.isRequired,
};

TrialBillingOverview.defaultProps = {
  servicePlan: null,
};

export default TrialBillingOverview;
